import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconDeviceFloppy, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { splitConsent, type RegistrationFormSettings } from '@creativitas/shared';
import { toUserMessage } from '@/lib/api';
import { ConsentCheckbox } from '@/features/auth/ConsentCheckbox';
import { PageHeader } from '@/components/ui/PageHeader';
import { ErrorState, LoadingState } from '@/components/ui/StateViews';
import { fetchRegistrationForm, saveRegistrationForm } from './api';

/**
 * Экран регистрации глазами того, кто отвечает за его тексты (§14).
 *
 * Согласия — юридические документы: они меняются, и менять их должна Дарья, а
 * не разработчик релизом. Поэтому здесь правятся и сами предложения, и то,
 * какие слова в них ведут на документ, и адреса документов.
 *
 * Рядом с полями — живой вид галочки, ровно тот, что увидит человек на
 * регистрации. Без него правка вслепую: ссылка ищется в тексте по словам, и
 * опечатка в слове тихо оставила бы документ без ссылки.
 */
export function AdminRegistrationPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['admin', 'registration'], queryFn: fetchRegistrationForm });

  const [draft, setDraft] = useState<RegistrationFormSettings | null>(null);
  useEffect(() => {
    if (query.data) setDraft(query.data);
  }, [query.data]);

  const save = useMutation({
    mutationFn: saveRegistrationForm,
    onSuccess: (saved) => {
      queryClient.setQueryData(['admin', 'registration'], saved);
      /* Форма регистрации читает ту же настройку — её кэш тоже устарел. */
      void queryClient.invalidateQueries({ queryKey: ['auth', 'registration-form'] });
    },
  });

  if (query.isPending) return <LoadingState />;
  if (query.isError || !draft) return <ErrorState onRetry={() => void query.refetch()} />;

  const patch = (change: Partial<RegistrationFormSettings>) =>
    setDraft({ ...draft, ...change });

  const patchConsent = (index: number, change: Partial<RegistrationFormSettings['consents'][number]>) =>
    patch({
      consents: draft.consents.map((consent, at) =>
        at === index ? { ...consent, ...change } : consent,
      ),
    });

  return (
    <>
      <PageHeader
        eyebrow="Администрирование"
        title="Экран регистрации"
        description="Надписи над формой и согласия. Изменения применяются к форме сразу; у новых согласий в журнале записывается текст и редакция на момент галочки."
        actions={
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            loading={save.isPending}
            onClick={() => save.mutate(draft)}
          >
            Сохранить
          </Button>
        }
      />

      <Stack gap="lg">
        {save.isError ? (
          <Alert color="red" icon={<IconAlertCircle size={17} />} title="Не удалось сохранить">
            {toUserMessage(save.error)}
          </Alert>
        ) : null}

        <Paper withBorder p="lg" radius="lg">
          <Stack gap="md">
            <Title order={4}>Надписи над формой</Title>
            <TextInput
              label="Заголовок"
              value={draft.heading}
              onChange={(event) => patch({ heading: event.currentTarget.value })}
            />
            <TextInput
              label="Подзаголовок"
              value={draft.subheading}
              onChange={(event) => patch({ subheading: event.currentTarget.value })}
            />
          </Stack>
        </Paper>

        {draft.consents.map((consent, index) => {
          const { missing } = splitConsent(consent);
          return (
            <Paper key={consent.id} withBorder p="lg" radius="lg">
              <Stack gap="md">
                <Group justify="space-between" wrap="wrap">
                  <Title order={4}>Согласие {index + 1}</Title>
                  {/*
                    Обязательное — на обработку данных: без него аккаунт не
                    заводится. Рассылки — по желанию: согласие на рекламу,
                    данное под принуждением, недействительно (docs/PRIVACY.md).
                  */}
                  <Switch
                    checked={consent.required}
                    onChange={(event) => patchConsent(index, { required: event.currentTarget.checked })}
                    label={consent.required ? 'Обязательное: без него регистрация невозможна' : 'По желанию'}
                  />
                </Group>

                <Textarea
                  label="Текст согласия"
                  description="Предложение целиком, как его прочитает человек."
                  autosize
                  minRows={2}
                  value={consent.text}
                  onChange={(event) => patchConsent(index, { text: event.currentTarget.value })}
                />

                <TextInput
                  label="Редакция документа"
                  description="Например, «от 12.10.2026». Записывается вместе с каждой новой галочкой — через год видно, с каким текстом человек соглашался. Меняйте при каждой новой редакции документа."
                  placeholder="от 12.10.2026"
                  value={consent.revision}
                  onChange={(event) => patchConsent(index, { revision: event.currentTarget.value })}
                />

                <Stack gap="xs">
                  <Text size="sm" fw={600}>Ссылки внутри текста</Text>
                  <Text size="xs" c="var(--text-muted)">
                    Слева — слова из текста выше, которые станут ссылкой. Справа — адрес документа.
                  </Text>

                  {consent.links.map((link, linkIndex) => (
                    <Group key={linkIndex} gap="xs" wrap="nowrap" align="flex-start">
                      <TextInput
                        style={{ flex: 1 }}
                        placeholder="Слова из текста"
                        value={link.label}
                        error={missing.some((item) => item === link) ? 'Этих слов нет в тексте' : undefined}
                        onChange={(event) =>
                          patchConsent(index, {
                            links: consent.links.map((item, at) =>
                              at === linkIndex ? { ...item, label: event.currentTarget.value } : item,
                            ),
                          })
                        }
                      />
                      <TextInput
                        style={{ flex: 2 }}
                        placeholder="https://creativitas.ru/..."
                        value={link.url}
                        onChange={(event) =>
                          patchConsent(index, {
                            links: consent.links.map((item, at) =>
                              at === linkIndex ? { ...item, url: event.currentTarget.value } : item,
                            ),
                          })
                        }
                      />
                      <ActionIcon
                        variant="light"
                        color="red"
                        aria-label="Убрать ссылку"
                        onClick={() =>
                          patchConsent(index, {
                            links: consent.links.filter((_, at) => at !== linkIndex),
                          })
                        }
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  ))}

                  <Group>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconPlus size={14} />}
                      onClick={() =>
                        patchConsent(index, { links: [...consent.links, { label: '', url: '' }] })
                      }
                    >
                      Добавить ссылку
                    </Button>
                  </Group>
                </Stack>

                <Stack gap={6}>
                  <Text size="sm" fw={600}>Как это увидит человек</Text>
                  <Paper withBorder p="sm" radius="md" style={{ background: 'var(--surface-elevated)' }}>
                    <ConsentCheckbox consent={consent} checked onChange={() => undefined} />
                  </Paper>
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </>
  );
}
